import type { ReactNode } from "react";
import { PspRules, SnakingCascadeCdtLevel } from "@CommonTypes/backend/PspRules";
import { RulesSection } from "./components/RulesSection";
import { RuleSelect } from "./components/RuleSelect";
import { Color, Flex, Grid, HorizontalDivider, NumberInput, Text } from "src/elements";
import { Tooltip } from "src/components/Tooltip/Tooltip";
import { RuleToggle } from "./components/RuleToggle";
import { RuleRange } from "./components/RuleRange";
import { RuleRadios } from "./components/RuleRadios";
import { frontFacingsOptions, frontFacingsTitle } from "./frontFacingsOptions";
import styled from "styled-components";
import { useTranslation } from "src/i18n";
import { DIMENSION } from "src/utils/formatDimension";

const TooltipText = styled(Text).attrs({ variant: "small2", color: Color.white })``;

interface Props {
  isDisabled?: boolean;
  /** The Dividers section, spanning both columns under the others. */
  dividersSection?: ReactNode;

  // Fixture
  canAdjustShelfNotches: PspRules["fixture"]["can_adjust_shelf_notches"];
  setCanAdjustShelfNotches: (value: PspRules["fixture"]["can_adjust_shelf_notches"]) => void;

  canAddOrDeleteShelves: PspRules["fixture"]["can_add_or_delete_shelves"];
  setCanAddOrDeleteShelves: (value: PspRules["fixture"]["can_add_or_delete_shelves"]) => void;

  shelvesSameHeightAsBasePog: PspRules["fixture"]["shelves_same_height_as_base_pog"];
  setShelvesSameHeightAsBasePog: (
    value: PspRules["fixture"]["shelves_same_height_as_base_pog"],
  ) => void;

  verticalOverlap: PspRules["fixture"]["vertical_overlap"];
  setVerticalOverlap: (value: PspRules["fixture"]["vertical_overlap"]) => void;

  mergedShelfWidthTolerancePct: PspRules["fixture"]["merged_shelf_width_tolerance_pct"];
  setMergedShelfWidthTolerancePct: (
    value: PspRules["fixture"]["merged_shelf_width_tolerance_pct"],
  ) => void;

  /** The pog's x100 length units, shown in inches. */
  fingerSpace: number;
  setFingerSpace: (value: number) => void;

  // Range
  canAddNonCoreRangeProducts: PspRules["range"]["can_add_non_core_range_products"];
  setCanAddNonCoreRangeProducts: (
    value: PspRules["range"]["can_add_non_core_range_products"],
  ) => void;

  canApplySqueezeToPlacedProducts: PspRules["range"]["can_apply_squeeze_to_placed_products"];

  /** Whether `range.min_candidate_quantity` is sent at all. */
  excludeSlowMovers: boolean;
  setExcludeSlowMovers: (value: boolean) => void;
  /** The floor sent while the switch is on: units sold per week. */
  minCandidateQuantity: number;
  setMinCandidateQuantity: (value: number) => void;

  /** Base-pog generation does not read `cdt_source`, so the control is hidden there. */
  isCdtSourceVisible: boolean;
  cdtSourceSummary?: ReactNode;
  cdtSource: NonNullable<PspRules["cdt_source"]>;
  setCdtSource: (value: NonNullable<PspRules["cdt_source"]>) => void;
  /** Whether CDT Studio has a release for this planogram, which is what the default follows. */
  hasCdtRelease: boolean;
  /**
   * What the CDT default followed, for the note under the choice. Left out, it
   * follows `hasCdtRelease`; "version" names a flow started from a CDT version.
   */
  cdtDefaultReason?: "release" | "version" | "dataFeed";
  setCanApplySqueezeToPlacedProducts: (
    value: PspRules["range"]["can_apply_squeeze_to_placed_products"],
  ) => void;

  // Merchandising
  verticalBlockingMode: PspRules["merchandising"]["vertical_blocking_mode"];
  setVerticalBlockingMode: (value: PspRules["merchandising"]["vertical_blocking_mode"]) => void;
  verticalBlockingModeDescription?: string;

  canBreakCasesTraysIfCoreRangeNotFit: PspRules["merchandising"]["can_break_cases_trays_if_core_range_not_fit"];
  setCanBreakCasesTraysIfCoreRangeNotFit: (
    value: PspRules["merchandising"]["can_break_cases_trays_if_core_range_not_fit"],
  ) => void;

  canFillExtraSpaceNextToCasesTraysWithLooseProducts: PspRules["merchandising"]["can_fill_extra_space_next_to_cases_trays_with_loose_products"];
  setCanFillExtraSpaceNextToCasesTraysWithLooseProducts: (
    value: PspRules["merchandising"]["can_fill_extra_space_next_to_cases_trays_with_loose_products"],
  ) => void;

  stackToShelfTop: boolean;
  setStackToShelfTop: (value: boolean) => void;

  stackPastMaxStack: boolean;
  setStackPastMaxStack: (value: boolean) => void;

  verticalCapping: boolean;
  setVerticalCapping: (value: boolean) => void;

  verticalCappingPastMaxStack: boolean;
  setVerticalCappingPastMaxStack: (value: boolean) => void;

  howShouldLooseProductsBePlacedWithRespectToTheirCasesTrays: PspRules["merchandising"]["how_should_loose_products_be_placed_with_respect_to_their_cases_trays"];
  setHowShouldLooseProductsBePlacedWithRespectToTheirCasesTrays: (
    value: PspRules["merchandising"]["how_should_loose_products_be_placed_with_respect_to_their_cases_trays"],
  ) => void;

  leverageAlternativeOrientations: PspRules["merchandising"]["leverage_alternative_orientations"];
  setLeverageAlternativeOrientations: (
    value: PspRules["merchandising"]["leverage_alternative_orientations"],
  ) => void;

  extraFacingMarginalValue: number;
  setExtraFacingMarginalValue: (value: number) => void;

  snaking: PspRules["merchandising"]["snaking"];
  setSnaking: (value: PspRules["merchandising"]["snaking"]) => void;

  snakingCascadeCdtLevel: SnakingCascadeCdtLevel | null;
  setSnakingCascadeCdtLevel: (value: SnakingCascadeCdtLevel | null) => void;

  cdtStacking: PspRules["merchandising"]["cdt_stacking"];
  setCdtStacking: (value: PspRules["merchandising"]["cdt_stacking"]) => void;

  itemsSqueezeOption: PspRules["merchandising"]["items_squeeze_option"];
  setItemsSqueezeOption: (value: PspRules["merchandising"]["items_squeeze_option"]) => void;
}

export const GeneralPlanogramRules = ({
  isDisabled,
  dividersSection,

  // Fixture
  canAdjustShelfNotches,
  setCanAdjustShelfNotches,
  canAddOrDeleteShelves,
  setCanAddOrDeleteShelves,
  shelvesSameHeightAsBasePog,
  setShelvesSameHeightAsBasePog,
  verticalOverlap,
  setVerticalOverlap,
  mergedShelfWidthTolerancePct,
  setMergedShelfWidthTolerancePct,
  fingerSpace,
  setFingerSpace,

  // Range
  canAddNonCoreRangeProducts,
  setCanAddNonCoreRangeProducts,
  canApplySqueezeToPlacedProducts,
  excludeSlowMovers,
  setExcludeSlowMovers,
  minCandidateQuantity,
  setMinCandidateQuantity,
  isCdtSourceVisible,
  cdtSource,
  cdtSourceSummary,
  setCdtSource,
  hasCdtRelease,
  cdtDefaultReason = hasCdtRelease ? "release" : "dataFeed",
  setCanApplySqueezeToPlacedProducts,

  // Merchandising
  verticalBlockingMode,
  setVerticalBlockingMode,
  verticalBlockingModeDescription,
  canBreakCasesTraysIfCoreRangeNotFit,
  setCanBreakCasesTraysIfCoreRangeNotFit,
  canFillExtraSpaceNextToCasesTraysWithLooseProducts,
  setCanFillExtraSpaceNextToCasesTraysWithLooseProducts,
  stackToShelfTop,
  setStackToShelfTop,
  stackPastMaxStack,
  setStackPastMaxStack,
  verticalCapping,
  setVerticalCapping,
  verticalCappingPastMaxStack,
  setVerticalCappingPastMaxStack,
  howShouldLooseProductsBePlacedWithRespectToTheirCasesTrays,
  setHowShouldLooseProductsBePlacedWithRespectToTheirCasesTrays,
  leverageAlternativeOrientations,
  setLeverageAlternativeOrientations,
  extraFacingMarginalValue,
  setExtraFacingMarginalValue,
  snaking,
  setSnaking,
  snakingCascadeCdtLevel,
  setSnakingCascadeCdtLevel,
  cdtStacking,
  setCdtStacking,
  itemsSqueezeOption,
  setItemsSqueezeOption,
}: Props) => {
  const { t } = useTranslation("common");
  const normalizedExtraFacingMarginalValue: number = extraFacingMarginalValue ?? 0;

  const cdtSourceOptions = [
    {
      label: t("planogramRules.cdtSource.storeProduct.label"),
      value: "STORE_PRODUCT",
      description: t("planogramRules.cdtSource.storeProduct.description"),
    },
    {
      label: t("planogramRules.cdtSource.categoryTree.label"),
      value: "CATEGORY_TREE",
      description: t("planogramRules.cdtSource.categoryTree.description"),
    },
  ] satisfies Array<{
    label: string;
    value: NonNullable<PspRules["cdt_source"]>;
    description: string;
  }>;

  const snakingCascadeCdtLevelOptions = [
    { label: t("planogramRules.snakingCascade.off"), value: null },
    { label: "cdt0", value: "cdt0" },
    { label: "cdt1", value: "cdt1" },
    { label: "cdt2", value: "cdt2" },
  ] satisfies Array<{ label: string; value: SnakingCascadeCdtLevel | null }>;

  return (
    <Grid
      width="100%"
      gap="20px"
      areas={[
        '"fixtures merchandising"',
        '"range    merchandising"',
        ...(isCdtSourceVisible ? ['"cdt      merchandising"'] : []),
        ...(dividersSection ? ['"dividers dividers"'] : []),
      ].join(" ")}
    >
      {/* Fixture Section */}
      <RulesSection title="Fixtures" area="fixtures" isDisabled={isDisabled}>
        <RuleToggle
          title="Can shelf notches be adjusted in stores for this review?"
          path="fixture.can_adjust_shelf_notches"
          value={canAdjustShelfNotches}
          setValue={setCanAdjustShelfNotches}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="Can we delete shelves to make products fit?"
          path="fixture.can_add_or_delete_shelves"
          value={canAddOrDeleteShelves}
          setValue={setCanAddOrDeleteShelves}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="Can we attempt to make shelves the same height as base pog?"
          path="fixture.shelves_same_height_as_base_pog"
          value={shelvesSameHeightAsBasePog}
          setValue={setShelvesSameHeightAsBasePog}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="Can products overlap with shelves vertically?"
          path="fixture.vertical_overlap"
          value={verticalOverlap}
          setValue={setVerticalOverlap}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleRange
          title="What is the merged shelf width tolerance percentage?"
          path="fixture.merged_shelf_width_tolerance_pct"
          min={0}
          max={30}
          step={0.01}
          value={mergedShelfWidthTolerancePct}
          setValue={setMergedShelfWidthTolerancePct}
          leftLabel="0%"
          rightLabel="30%"
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <Flex column justify="center" padding="10px" gap="10px">
          <Text variant="small1" textAlign="center" isDisabled={isDisabled}>
            {t("planogramRules.fingerSpace.title")}
          </Text>

          <Text variant="small2" textAlign="center" isDisabled={isDisabled}>
            {t("planogramRules.fingerSpace.description")}
          </Text>

          <NumberInput
            title={t("planogramRules.fingerSpace.title")}
            value={fingerSpace / DIMENSION.INCH}
            setValue={(inches) =>
              setFingerSpace(inches > 0 ? Math.round(inches * DIMENSION.INCH) : 0)
            }
            min={0}
            isDisabled={isDisabled}
          />
        </Flex>
      </RulesSection>

      {/* Merchandising Section */}
      <RulesSection title="Merchandising" area="merchandising" isDisabled={isDisabled}>
        <RuleToggle
          title="Use vertical blocking mode?"
          testId="rule-vertical-blocking"
          description={verticalBlockingModeDescription}
          path="merchandising.vertical_blocking_mode"
          value={verticalBlockingMode}
          setValue={setVerticalBlockingMode}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="Can we break trays if a core range product cannot fit otherwise?"
          path="merchandising.can_break_cases_trays_if_core_range_not_fit"
          value={canBreakCasesTraysIfCoreRangeNotFit}
          setValue={setCanBreakCasesTraysIfCoreRangeNotFit}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title={t("planogramRules.fillExtraSpace.title")}
          description={t("planogramRules.fillExtraSpace.description")}
          path="merchandising.can_fill_extra_space_next_to_cases_trays_with_loose_products"
          value={canFillExtraSpaceNextToCasesTraysWithLooseProducts}
          setValue={setCanFillExtraSpaceNextToCasesTraysWithLooseProducts}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title={t("planogramRules.stackToShelfTop.title")}
          description={t("planogramRules.stackToShelfTop.description")}
          testId="rule-stack-to-shelf-top"
          path="merchandising.stack_to_shelf_top"
          value={stackToShelfTop}
          setValue={setStackToShelfTop}
          isDisabled={isDisabled}
        />

        {stackToShelfTop && (
          <RuleToggle
            title={t("planogramRules.stackPastMaxStack.title")}
            description={t("planogramRules.stackPastMaxStack.description")}
            testId="rule-stack-past-max-stack"
            path="merchandising.stack_past_max_stack"
            value={stackPastMaxStack}
            setValue={setStackPastMaxStack}
            isDisabled={isDisabled}
          />
        )}

        <HorizontalDivider />

        <RuleToggle
          title={t("planogramRules.verticalCapping.title")}
          description={t("planogramRules.verticalCapping.description")}
          testId="rule-vertical-capping"
          path="merchandising.vertical_capping"
          value={verticalCapping}
          setValue={setVerticalCapping}
          isDisabled={isDisabled}
        />

        {verticalCapping && (
          <RuleToggle
            title={t("planogramRules.verticalCappingPastMaxStack.title")}
            description={t("planogramRules.verticalCappingPastMaxStack.description")}
            testId="rule-vertical-capping-past-max-stack"
            path="merchandising.vertical_capping_past_max_stack"
            value={verticalCappingPastMaxStack}
            setValue={setVerticalCappingPastMaxStack}
            isDisabled={isDisabled}
          />
        )}

        <HorizontalDivider />

        <RuleRadios
          title="How should loose products be placed with respect to their cases / trays?"
          path="merchandising.how_should_loose_products_be_placed_with_respect_to_their_cases_trays"
          options={[
            { label: "Behind", value: "BEHIND" },
            { label: "Front", value: "FRONT" },
          ]}
          value={howShouldLooseProductsBePlacedWithRespectToTheirCasesTrays}
          setValue={setHowShouldLooseProductsBePlacedWithRespectToTheirCasesTrays}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleRadios
          title={frontFacingsTitle(t)}
          path="merchandising.leverage_alternative_orientations"
          options={frontFacingsOptions(t)}
          value={leverageAlternativeOrientations}
          setValue={setLeverageAlternativeOrientations}
          isDisabled={isDisabled}
          direction="column"
        />

        <HorizontalDivider />

        <RuleRange
          title="Extra Facing Marginal Value"
          path="merchandising.extra_facing_marginal_value"
          min={0}
          max={1}
          step={0.01}
          value={normalizedExtraFacingMarginalValue}
          setValue={setExtraFacingMarginalValue}
          leftLabel="0"
          rightLabel="1"
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="Allow snaking?"
          path="merchandising.snaking"
          value={snaking}
          setValue={setSnaking}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleSelect
          title={t("planogramRules.snakingCascade.title")}
          path="merchandising.snaking_cascade_cdt_level"
          tooltip={t("planogramRules.snakingCascade.description")}
          options={snakingCascadeCdtLevelOptions}
          value={snakingCascadeCdtLevel}
          setValue={setSnakingCascadeCdtLevel}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="Allow CDT stacking?"
          path="merchandising.cdt_stacking"
          value={cdtStacking}
          setValue={setCdtStacking}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleRadios
          title="Should we squeeze product dimensions?"
          path="merchandising.items_squeeze_option"
          options={[
            { label: "No Squeeze", value: "none" },
            { label: "Squeeze All", value: "all" },
          ]}
          value={itemsSqueezeOption}
          setValue={setItemsSqueezeOption}
          isDisabled={isDisabled}
        />
      </RulesSection>

      {/* Range Section */}
      <RulesSection title="Range" area="range" isDisabled={isDisabled}>
        <RuleToggle
          title="Can we add non-core range products to fill underutilised shelf space?"
          path="range.can_add_non_core_range_products"
          value={canAddNonCoreRangeProducts}
          setValue={setCanAddNonCoreRangeProducts}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="Can we squeeze already placed products to make room for new products?"
          path="range.can_apply_squeeze_to_placed_products"
          value={canApplySqueezeToPlacedProducts}
          setValue={setCanApplySqueezeToPlacedProducts}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title={t("planogramRules.slowMovers.title")}
          description={t("planogramRules.slowMovers.description")}
          path="range.min_candidate_quantity"
          testId="rule-exclude-slow-movers"
          value={excludeSlowMovers}
          setValue={setExcludeSlowMovers}
          isDisabled={isDisabled}
        />

        {excludeSlowMovers && (
          <Flex column justify="center" padding="0 10px 10px" gap="10px">
            <NumberInput
              title={t("planogramRules.slowMovers.threshold")}
              value={minCandidateQuantity}
              setValue={(quantity) => setMinCandidateQuantity(quantity > 0 ? quantity : 0)}
              min={0}
              step={0.05}
              isDisabled={isDisabled}
            />
          </Flex>
        )}
      </RulesSection>

      {dividersSection}

      {/* CDT Section */}
      {isCdtSourceVisible && (
        <RulesSection
          title={t("planogramRules.cdtSource.sectionTitle")}
          area="cdt"
          isDisabled={isDisabled}
        >
          <RuleSelect
            title={t("planogramRules.cdtSource.question")}
            path="cdt_source"
            options={cdtSourceOptions}
            value={cdtSource}
            setValue={setCdtSource}
            isDisabled={isDisabled}
            tooltip={
              <Tooltip width="360px">
                <Flex column gap="5px">
                  {cdtSourceOptions.map((option) => (
                    <TooltipText key={option.value}>
                      <b>{option.label}:</b> {option.description}
                    </TooltipText>
                  ))}
                </Flex>
              </Tooltip>
            }
          />

          {cdtSource === "CATEGORY_TREE" && cdtSourceSummary}

          <Flex padding="0 10px 10px" justify="center">
            <Text variant="small2" color={Color.textSecondary} textAlign="center">
              {cdtDefaultReason === "release"
                ? t("planogramRules.cdtSource.defaultedToCategoryTree")
                : cdtDefaultReason === "version"
                  ? t("planogramRules.cdtSource.defaultedToCategoryTreeFromVersion")
                  : t("planogramRules.cdtSource.defaultedToStoreProduct")}
            </Text>
          </Flex>
        </RulesSection>
      )}
    </Grid>
  );
};
